// Agent dialer — opened from the More Setting menu.
// Outbound calls always present the agent's own number; the carrier behind it
// is resolved by the backend.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { parsePhoneNumber, isValidPhoneNumber } from "libphonenumber-js";
import styles from "./Dialer.module.css";
import DialerHeader from "./DialerHeader";
import NumberDisplay from "./NumberDisplay";
import Keypad from "./Keypad";
import CallButton from "./CallButton";
import CallScreen from "./CallScreen";
import IncomingCall from "./IncomingCall";
import DialerTabs from "./DialerTabs";
import RecentsList from "./RecentsList";
import ContactsList from "./ContactsList";
import useDialer from "./hooks/useDialer";
import useTeamPermissions from "../../hooks/useTeamPermissions";
import { trackEvent } from "../../lib/analytics";

const IN_CALL_STATES = ["calling", "ringing", "active", "held", "busy", "failed"];
const CALL_DEBOUNCE_MS = 1000;
// E.164 me kisi bhi number me 15 se zyada digit hote hi nahi — "+" ke saath 16.
const MAX_NUMBER_LENGTH = 16;

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const authToken = () => localStorage.getItem("token") || "";

// Team member ke liye backend ko owner batana padta hai, warna contacts 400 dete hain.
const contactOwnerId = () =>
  sessionStorage.getItem("SelectAgentOwnerId") || localStorage.getItem("teamOwnerId") || "";

// Number contacts me saved ho to uska naam, warna khaali.
const lookupContactName = async (phone) => {
  try {
    const owner = contactOwnerId();
    const url = `${API_BASE}/contact-routes/contacts/lookup${
      owner ? `?ownerId=${encodeURIComponent(owner)}` : ""
    }`;
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken()}`,
      },
      body: JSON.stringify({ phone }),
    });
    if (!res.ok) return "";
    const data = await res.json().catch(() => ({}));
    return data?.contact_name || "";
  } catch (_) {
    return "";
  }
};

const validateNumber = (raw) => {
  const cleaned = String(raw || "").trim();
  if (!cleaned) return { valid: false, error: "", suggestion: null };

  if (!cleaned.startsWith("+")) {
    const digits = cleaned.replace(/\D/g, "");
    if (digits.length === 10) {
      return { valid: false, error: "Country code missing. Did you mean +1?", suggestion: `+1${digits}` };
    }
    if (digits.length === 11 && digits.startsWith("1")) {
      return { valid: false, error: "Missing + sign", suggestion: `+${digits}` };
    }
    return { valid: false, error: "Start with + and the country code (e.g. +1)", suggestion: null };
  }

  try {
    if (!isValidPhoneNumber(cleaned)) {
      let hint = "Invalid number for this country code";
      try {
        const parsed = parsePhoneNumber(cleaned);
        if (parsed?.country) hint = `Invalid number for ${parsed.country}`;
      } catch (_) {}
      return { valid: false, error: hint, suggestion: null };
    }
    const e164 = parsePhoneNumber(cleaned).format("E.164");
    return { valid: true, error: "", formatted: e164, suggestion: cleaned !== e164 ? e164 : null };
  } catch (_) {
    return { valid: false, error: "Invalid phone number", suggestion: null };
  }
};

const Dialer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const agentId =
    location.state?.agentId ||
    (typeof window !== "undefined" ? sessionStorage.getItem("SelectAgentId") : null) ||
    null;

  const [number, setNumber] = useState("");
  const [numberError, setNumberError] = useState("");
  const [suggestion, setSuggestion] = useState("");
  const [tab, setTab] = useState("keypad");
  const [pendingGoogleSync, setPendingGoogleSync] = useState(false);
  // Call ke dauraan keypad `number` ko badal deta hai (DTMF), isliye jis number
  // pe call gayi wo alag rakha hai — naam aur title dono isi se bante hain.
  const [callTarget, setCallTarget] = useState("");
  const [contactName, setContactName] = useState("");
  const lastCallAttemptRef = useRef(0);
  const wasInCallRef = useRef(false);

  const {
    deviceStatus,
    callState,
    incomingFrom,
    formattedDuration,
    muted,
    held,
    speakerOn,
    lastError,
    callEndReason,
    makeCall,
    hangUp,
    acceptCall,
    rejectCall,
    toggleMute,
    toggleHold,
    toggleSpeaker,
    sendDtmf,
    retryInit,
    config,
    configLoading,
    callerId,
    callerIds,
    setCallerId,
    blocked,
    blockedMessage,
    reloadConfig,
  } = useDialer({ agentId });

  const { canViewContacts } = useTeamPermissions();

  // Prefill when another screen sends a number to dial.
  useEffect(() => {
    if (location.state?.number) {
      setNumber(String(location.state.number).slice(0, MAX_NUMBER_LENGTH));
    }
  }, [location.state]);

  // If the Google contacts OAuth hands us back with ?google_sync=success (the
  // native app can restore the WebView on this URL), open the Contacts tab and
  // let ContactsList finish the sync. URL is cleaned first so a refresh doesn't
  // re-trigger it. In a plain browser the backend returns to /contacts instead,
  // where ContactsPage runs the same sync.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("google_sync") !== "success") return;
    // Google Contacts authorised, returned to the dialer. Same event the
    // /contacts handler fires; the once-per-user guard in trackEvent stops a
    // double count if both handlers see the same return.
    trackEvent("google_contacts_connected", { source: "dialer" });
    setTab("contacts");
    setPendingGoogleSync(true);
    navigate(location.pathname, { replace: true, state: location.state });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keypad, physical keyboard aur paste — sab yahin se aate hain, isliye
  // length ka cap yahi ek jagah lagta hai. Limit ke baad ke digits chup-chaap
  // ignore hote hain (phone dialers ka standard behaviour).
  const updateNumber = useCallback((value) => {
    const cleaned = String(value ?? "").replace(/[^\d+*#]/g, "");
    // "+" sirf number ke aage chalta hai — kahin aur pada ho to aage le aao.
    const next = (cleaned.includes("+")
      ? `+${cleaned.replace(/\+/g, "")}`
      : cleaned
    ).slice(0, MAX_NUMBER_LENGTH);
    setNumber(next);
    if (!next || next.length < 6) {
      setNumberError("");
      setSuggestion("");
      return;
    }
    const result = validateNumber(next);
    setNumberError(result.valid ? "" : result.error);
    setSuggestion(result.suggestion || "");
  }, []);

  const handleCall = useCallback(() => {
    if ([...IN_CALL_STATES, "incoming"].includes(callState)) return;
    const now = Date.now();
    if (now - lastCallAttemptRef.current < CALL_DEBOUNCE_MS) return;
    lastCallAttemptRef.current = now;

    const result = validateNumber(number);
    if (!result.valid) {
      setNumberError(result.error);
      if (result.suggestion) setSuggestion(result.suggestion);
      return;
    }
    setNumberError("");
    setSuggestion("");
    setCallTarget(result.formatted);
    if (document.activeElement && typeof document.activeElement.blur === "function") {
      document.activeElement.blur();
    }
    makeCall(result.formatted);
  }, [number, makeCall, callState]);

  const handleCallRef = useRef(handleCall);
  useEffect(() => {
    handleCallRef.current = handleCall;
  }, [handleCall]);

  // Recents/Contacts se number sirf keypad me bharta hai — call user khud dabaye.
  const fillNumber = useCallback(
    (raw) => {
      const value = String(raw || "").trim();
      if (!value) return;
      updateNumber(value);
      setTab("keypad");
    },
    [updateNumber]
  );

  // Physical keyboard support on desktop.
  useEffect(() => {
    const onKey = (e) => {
      if ([...IN_CALL_STATES, "incoming"].includes(callState)) return;
      if (["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;

      if (/^[0-9*#+]$/.test(e.key)) updateNumber(number + e.key);
      else if (e.key === "Backspace") updateNumber(number.slice(0, -1));
      else if (e.key === "Enter") handleCallRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [callState, number, updateNumber]);

  const handleKeypadPress = useCallback(
    (digit) => {
      if (callState === "active" || callState === "held") sendDtmf(digit);
      updateNumber(number + digit);
    },
    [callState, sendDtmf, number, updateNumber]
  );

  // Permission na ho (ya beech me chali jaye) to Contacts tab pe atke mat raho.
  useEffect(() => {
    if (!canViewContacts && tab === "contacts") setTab("keypad");
  }, [canViewContacts, tab]);

  // Inbound call ka number bhi target ban jaata hai, taaki uthane ke baad naam bana rahe.
  useEffect(() => {
    if (incomingFrom) setCallTarget(incomingFrom);
  }, [incomingFrom]);

  // Saved contact ka naam — na mile to CallScreen number hi dikhata hai.
  useEffect(() => {
    if (!callTarget) {
      setContactName("");
      return;
    }
    let cancelled = false;
    lookupContactName(callTarget).then((name) => {
      if (!cancelled) setContactName(name);
    });
    return () => {
      cancelled = true;
    };
  }, [callTarget]);

  // Call khatam hote hi keypad khaali — call ke dauraan DTMF ke digits bhi isi
  // field me judte hain, isliye purana number chhoda to agli dial ganda milta hai.
  useEffect(() => {
    if (IN_CALL_STATES.includes(callState) || callState === "incoming") {
      wasInCallRef.current = true;
      return;
    }
    // Screen abhi khuli hi hai, koi call hui hi nahi — kuch saaf karne ko nahi.
    if (!wasInCallRef.current) return;
    wasInCallRef.current = false;
    setNumber("");
    setNumberError("");
    setSuggestion("");
  }, [callState]);

  const goBack = useCallback(() => {
    if (IN_CALL_STATES.includes(callState)) hangUp();
    navigate(-1);
  }, [callState, hangUp, navigate]);

  if (callState === "incoming") {
    return (
      <div className={styles.page}>
        <div className={styles.shell}>
          <IncomingCall
            from={incomingFrom}
            contactName={contactName}
            onAccept={acceptCall}
            onReject={rejectCall}
          />
        </div>
      </div>
    );
  }

  if (IN_CALL_STATES.includes(callState)) {
    return (
      <div className={styles.page}>
        <div className={styles.shell}>
          <CallScreen
            number={callTarget || number || incomingFrom}
            contactName={contactName}
            callerId={callerId}
            callState={callState}
            duration={formattedDuration}
            muted={muted}
            held={held}
            speakerOn={speakerOn}
            onMute={toggleMute}
            onHold={toggleHold}
            onSpeaker={toggleSpeaker}
            onHangUp={hangUp}
            onKeypad={sendDtmf}
          />
        </div>
      </div>
    );
  }

  const isValid = number.trim().length > 0 && validateNumber(number).valid;

  return (
    <div className={styles.page}>
      <div className={styles.shell}>
        <DialerHeader
          deviceStatus={blocked ? "notready" : deviceStatus}
          onBack={goBack}
          onRetry={retryInit}
        />

        {lastError && deviceStatus === "error" && !blocked ? (
          <div className={styles.errorBanner}>
            <span>{lastError}</span>
            <button type="button" onClick={retryInit}>
              Retry
            </button>
          </div>
        ) : null}

        {deviceStatus === "reconnecting" ? (
          <div className={styles.warnBanner}>Reconnecting…</div>
        ) : null}

        {callEndReason === "remote" ? (
          <div className={styles.infoBanner}>Call ended by the other party</div>
        ) : null}
        {callEndReason === "busy" ? <div className={styles.warnBanner}>Line was busy</div> : null}
        {callEndReason === "no-answer" ? <div className={styles.warnBanner}>No answer</div> : null}

        {configLoading ? (
          <div className={styles.centerState}>
            <span className={styles.spinner} />
            <p>Setting up your dialer…</p>
          </div>
        ) : blocked ? (
          <div className={styles.blockedWrap}>
            <div className={styles.blockedCard}>
              <span className={styles.blockedIcon}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 9v4.5m0 3h.01M10.3 4.2 2.9 17a2 2 0 0 0 1.7 3h14.8a2 2 0 0 0 1.7-3L13.7 4.2a2 2 0 0 0-3.4 0Z"
                    stroke="#B26A00"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>

              <h2 className={styles.blockedTitle}>Dialer not ready</h2>

              {/* Jis number pe dikkat hai wahi dikha do — guess na karna pade. */}
              {config?.callerId ? (
                <span className={styles.blockedNumber}>{config.callerId}</span>
              ) : null}

              <p className={styles.blockedText}>
                {blockedMessage || "No number is assigned to this agent yet."}
              </p>

              <div className={styles.blockedActions}>
                {/* <button
                  type="button"
                  className={styles.primaryBtn}
                  onClick={() => navigate("/assign-number")}
                >
                  Assign a number
                </button> */}  
                <button type="button" className={styles.ghostBtn} onClick={reloadConfig}>
                  Check again
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {tab === "keypad" ? (
              <div className={styles.body}>
                <NumberDisplay
                  number={number}
                  callerId={callerId}
                  callerIds={callerIds}
                  onCallerIdChange={setCallerId}
                  onChange={updateNumber}
                  onDelete={() => updateNumber(number.slice(0, -1))}
                  onLongDelete={() => updateNumber("")}
                />

                {numberError ? (
                  <div className={styles.validationRow}>
                    <span className={styles.validationError}>{numberError}</span>
                    {suggestion ? (
                      <button
                        type="button"
                        className={styles.suggestionBtn}
                        onClick={() => updateNumber(suggestion)}
                      >
                        Use {suggestion}
                      </button>
                    ) : null}
                  </div>
                ) : null}

                <div className={styles.keypadSection}>
                  <Keypad
                    onPress={handleKeypadPress}
                    onLongPressZero={() => updateNumber(`+${number}`)}
                  />
                  <CallButton
                    disabled={!isValid || deviceStatus !== "ready" || !!numberError}
                    onCall={handleCall}
                    deviceStatus={deviceStatus}
                  />
                </div>
              </div>
            ) : tab === "recents" ? (
              <RecentsList onPick={fillNumber} />
            ) : canViewContacts ? (
              <ContactsList
                onPick={fillNumber}
                agentId={agentId}
                pendingGoogleSync={pendingGoogleSync}
                onGoogleSyncHandled={() => setPendingGoogleSync(false)}
              />
            ) : null}

            <DialerTabs
              active={tab}
              onChange={setTab}
              hidden={canViewContacts ? [] : ["contacts"]}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default Dialer;
